import { useState } from 'react';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import {
    AdditionalCostCorrectionDetailsDto,
    AdditionalCostDetailsDto,
    ChildId,
    CostAllocationId,
} from '../../../../../../types';
import {
    AppCardListItem,
    AppTitle,
    DetailsColumn,
    DetailsFrameBase,
    DetailsRow,
} from '../../../../../atoms';
import { AppCardList, AppForm, RemoveModal } from '../../../../../organisms';
import { commonTexts, settlementTexts } from '../../../../../../consts/text';
import { getFormattedPrice } from '../../../../../../utils/getFormattedPrice';
import { payerMapper } from '../../../../../../consts/settlementChildren/payerMapper';
import { AddCostSplitModal } from '../../../AddEditCostSplitModal/AddCostSplitModal/AddCostSplitModal';
import { EditCostSplitModal } from '../../../AddEditCostSplitModal/EditCostSplitModal/EditCostSplitModal';
import { useAppMutation } from '../../../../../../services/reactQuery/useAppMutation';
import StaffHTTPService from '../../../../../../services/HTTPService/staff/StaffHTTPService';
import { useSimpleNotification } from '../../../../../../hooks/useSimpleNotification/useSimpleNotification';
import { ChildCorrectionInvoices } from '../../../ChildCorrectionInvoices/ChildCorrectionInvoices';
import { InvoiceCorrectionUploadElement } from '../../../../../molecules/FormElements/InvoiceCorrectionUploadElement/InvoiceCorrectionUploadElement';
import { EditAdditionalCostCorrectionModal } from './EditAdditionalCostCorrectionModal/EditAdditionalCostCorrectionModal';
import { AdditionalCostCorrectionInvoices } from '../AdditionalCostItem/AdditionalCostDetails/AdditionalCostCorrectionInvoices';

type Props = {
    correction: AdditionalCostCorrectionDetailsDto;
    detailsDto: AdditionalCostDetailsDto;
    childId: ChildId;
    index: number;
};

export const AdditionalCostCorrectionItem = ({ correction, detailsDto, index, childId }: Props) => {
    const [manualBillEditModalOpen, setManualBillEditModalOpen] = useState(false);
    const [openAddCostSplitModal, setOpenAddCostSplitModal] = useState(false);
    const [editCostSplitId, setEditCostSplitId] = useState<CostAllocationId>();
    const [removeCostAllocationId, setRemoveCostAllocationId] = useState<CostAllocationId | null>(
        null,
    );
    const { displaySuccess, displayError } = useSimpleNotification();
    const isMobile = useIsMobile();

    const correctionCostSplits = correction?.costSplits;

    const { mutate: removeCostAllocation, isLoading: isRemoveCostAllocationLoading } =
        useAppMutation(StaffHTTPService.settlements.removeCostAllocation, {
            key: ['REMOVE_COST_ALLOCATION'],
            onSuccess: () => {
                displaySuccess(settlementTexts.children.costAllocation.removeSuccess);
                setRemoveCostAllocationId(null);
            },
            onError: () => {
                displayError(commonTexts.errorMessages.unknown);
            },
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['COST_ALLOCATION'],
                ['CHILD_SETTLEMENT', childId],
                ['CHILD_SETTLEMENT_COST_SPLIT', childId],
            ],
        });

    const onRemoveCancel = () => setRemoveCostAllocationId(null);
    const onRemoveOk = () =>
        removeCostAllocationId &&
        removeCostAllocation({ costAllocationId: removeCostAllocationId });
    const handleDeleteCostAllocation = (costAllocationId: string) =>
        setRemoveCostAllocationId(costAllocationId as CostAllocationId);

    if (!correction) return null;

    return (
        <>
            <RemoveModal
                open={!!removeCostAllocationId}
                title={settlementTexts.children.costAllocationRemove}
                onOk={onRemoveOk}
                onCancel={onRemoveCancel}
                confirmActionLoading={isRemoveCostAllocationLoading}
            />

            <DetailsFrameBase key={correction.id}>
                <AppTitle level={4} marginTop={-16} marginBottom={16}>
                    {`${correction.verified ? 'Korekta' : 'Propozycja korekty'} ${
                        correction.orderNumber
                    }: ${detailsDto.name}`}
                </AppTitle>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 16,
                        alignItems: 'flex-start',
                        marginBottom: 16,
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <AppCardListItem
                            key={correction.id}
                            index={index}
                            listItem={undefined}
                            options={
                                !correction.verified
                                    ? [
                                          {
                                              label: 'Edytuj',
                                              onClick: () => setManualBillEditModalOpen(true),
                                          },
                                      ]
                                    : []
                            }
                        >
                            <DetailsRow>
                                <DetailsColumn
                                    title={
                                        settlementTexts.children.manualBill.settlementDetails
                                            .newAmount
                                    }
                                    span={6}
                                >
                                    {correction.grossSummary
                                        ? `${correction.grossSummary} zł`
                                        : '-'}
                                </DetailsColumn>
                                <DetailsColumn
                                    title={
                                        settlementTexts.children.manualBill.settlementDetails
                                            .amountDifference
                                    }
                                    span={6}
                                >
                                    {correction.grossSummary
                                        ? `${correction.grossAmountDifference} zł`
                                        : '-'}
                                </DetailsColumn>
                            </DetailsRow>
                        </AppCardListItem>
                    </div>

                    {correction.verified && (
                        <div
                            style={{
                                width: isMobile ? '100%' : 280,
                                flexShrink: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0,
                                marginTop: -32,
                            }}
                        >
                            <ChildCorrectionInvoices
                                fileName={correction.fileName}
                                correctionId={correction.id}
                            />
                            {correction.invoiceData && (
                                <AdditionalCostCorrectionInvoices
                                    invoices={correction.invoiceData}
                                />
                            )}
                        </div>
                    )}

                    {correction.verified && (
                        <div
                            style={{
                                width: isMobile ? '100%' : 240,
                                flexShrink: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                marginTop: -32,
                            }}
                        >
                            <AppForm name="file" withHeader={false} maxWidth={440}>
                                <InvoiceCorrectionUploadElement
                                    correctionId={correction.id}
                                    label="Dodaj plik korekty"
                                    accept=".pdf"
                                />
                            </AppForm>
                        </div>
                    )}
                </div>

                <AppCardList
                    marginTop={16}
                    title="Podział kosztów"
                    dataSource={correctionCostSplits ?? []}
                    addButtonLabel="Dodaj podział kosztów"
                    onAddButtonClick={() => setOpenAddCostSplitModal(true)}
                    isHiddenAddButton={!!correction?.verified}
                    emptyInfo={settlementTexts.children.costAllocation.emptyInfo}
                    renderItem={(record, idx) => (
                        <AppCardListItem
                            key={record.id}
                            index={idx}
                            listItem={record}
                            options={
                                !correction.verified
                                    ? [
                                          {
                                              label: 'Edytuj',
                                              Icon: <IconEdit size={20} />,
                                              onClick: () =>
                                                  setEditCostSplitId(record.id as CostAllocationId),
                                          },
                                          {
                                              label: 'Usuń',
                                              Icon: <IconTrash size={20} />,
                                              onClick: () => handleDeleteCostAllocation(record.id),
                                          },
                                      ]
                                    : undefined
                            }
                        >
                            <DetailsRow>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.institutionName}
                                    span={16}
                                >
                                    {record.foundationName}
                                </DetailsColumn>
                            </DetailsRow>
                            <DetailsRow>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.nip}
                                    span={6}
                                >
                                    {record.nip}
                                </DetailsColumn>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.percentageAmount}
                                    span={6}
                                >
                                    {record.percentageAmount ? `${record.percentageAmount} %` : '-'}
                                </DetailsColumn>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.finalAmount}
                                    span={6}
                                >
                                    {getFormattedPrice(record.finalGrossAmount || 0)}
                                </DetailsColumn>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.payerType}
                                    span={6}
                                >
                                    {payerMapper[record.payerType]}
                                </DetailsColumn>
                            </DetailsRow>
                            <DetailsRow>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.refersTo}
                                    span={6}
                                >
                                    {record.sourceName}
                                </DetailsColumn>
                                <DetailsColumn
                                    title={settlementTexts.children.costSplit.invoiceNotes}
                                    span={12}
                                >
                                    {record.invoiceNotes}
                                </DetailsColumn>
                            </DetailsRow>
                        </AppCardListItem>
                    )}
                />
            </DetailsFrameBase>

            <EditAdditionalCostCorrectionModal
                isOpen={manualBillEditModalOpen}
                onCancel={() => setManualBillEditModalOpen(false)}
                correctionId={correction.id}
                additionalCostId={detailsDto.id}
                grossSummary={correction.grossSummary}
            />
            <AddCostSplitModal
                type="additional-costs"
                isOpen={openAddCostSplitModal}
                onClose={() => setOpenAddCostSplitModal(false)}
                childId={childId}
                correctionId={correction.id}
            />
            {editCostSplitId && (
                <EditCostSplitModal
                    type="additional-costs"
                    isOpen={!!editCostSplitId}
                    onClose={() => setEditCostSplitId(undefined)}
                    childId={childId}
                    additionalCostId={detailsDto.id}
                    costAllocationId={editCostSplitId}
                    correctionId={correction.id}
                />
            )}
        </>
    );
};
